import './styles.scss';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, IButtonProps } from '@components/Button/Button';
import { Form } from '@components/Form/Form';
import { IFormItem } from '@components/Form/FormItem/FormItem';
import { Card as AntCard, Spin, Tooltip, Typography } from 'antd';
import { ButtonType } from 'antd/es/button';
import React, { ReactNode } from 'react';

import { Auth, hasPermission, securityConfig } from '@/shared/security/hasPermission';
import { withSecurity } from '@/shared/security/withSecurity';
import { generateSimpleKey } from '@/utils/utils';

export interface ICardProps {
  viewMode?: boolean;
  schema: ICardItem[];
  title?: string;
  alarm?: string;
  data?: Record<string, any>;
  loading?: boolean;
  onSubmit?: (values: any) => void;
  onCancel?: () => void;
  layout?: {
    formLayout: 'horizontal' | 'vertical';
    itemLayout: 'horizontal' | 'vertical';
    rowGutter?: number;
    colSpan?: number;
  };
  actions?: {
    onEdit?: { action: () => void; security?: securityConfig; display?: boolean };
    onBack?: () => void;
    extra?: {
      text: string;
      action: () => void;
      buttonProps?: Omit<IButtonProps, 'key' | 'text' | 'action'>;
      security?: securityConfig;
    }[];
  };
  buttons?: {
    submit?: {
      text?: string;
      type?: ButtonType;
    };
    cancel?: {
      text?: string;
      type?: ButtonType;
    };
    loading?: boolean;
  };
  columns?: number;
  variant?: 'borderless' | 'outlined' | undefined;
  children?: ReactNode | ReactNode[];
  ref?: any;
  id?: string;
  styles?: React.CSSProperties | undefined;
  auth: Auth;
}

export type ICardItem = IFormItem;

const CardComponent: React.FC<ICardProps> = withSecurity(
  ({
    viewMode = false,
    schema,
    title,
    alarm,
    data,
    loading = false,
    onSubmit,
    onCancel,
    layout,
    actions,
    buttons,
    variant,
    ref,
    children,
    id,
    styles,
    auth,
  }: ICardProps) => {
    const renderCardButtons = () => {
      if (!actions) return null;

      return (
        <div className="card-buttons-wrapper">
          <div className="back-button">
            {actions?.onBack && (
              <Button text="Назад" variant={'outlined'} action={actions.onBack} />
            )}
          </div>
          <div className="action-buttons">
            {actions?.extra &&
              actions.extra
                .filter((btn) => hasPermission(auth, btn?.security))
                .map((button, index) => (
                  <Button
                    key={index}
                    text={button.text}
                    action={button.action}
                    {...button.buttonProps}
                  />
                ))}
            {actions?.onEdit && hasPermission(auth, actions.onEdit.security) && (
              <Button
                type="primary"
                text="Редактировать"
                action={actions.onEdit.action}
                display={actions.onEdit.display}
              />
            )}
          </div>
        </div>
      );
    };

    return (
      <Spin spinning={loading}>
        <div className="card-wrapper">
          <div className="card-header">{renderCardButtons()}</div>
          <AntCard
            style={styles}
            className="card-content"
            variant={variant}
            loading={loading}>
            {title && (
              <Typography.Title className={'card-title'}>
                {title}
                {title && alarm && (
                  <Tooltip
                    placement="top"
                    title={<div className={'card-alarm'}>{alarm}</div>}
                    color={'red'}>
                    <ExclamationCircleFilled className={'card-exclamation'} />
                  </Tooltip>
                )}
              </Typography.Title>
            )}
            <div className="card-body">
              <Form
                key={id || generateSimpleKey()}
                ref={ref}
                viewMode={viewMode}
                schema={schema}
                data={data}
                onSubmit={onSubmit}
                onCancel={onCancel}
                layout={layout}
                buttons={buttons}
                labelWrap
                labelAlign={'left'}
              />
              {children}
            </div>
          </AntCard>
        </div>
      </Spin>
    );
  },
);

export const Card = withSecurity(CardComponent);
